import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bus, Plane, User, Building2, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { locationChipStyle } from '@/lib/locationColors';

// Verdier brytes alltid over flere linjer — aldri «…»-avkorting som skjuler flynummer eller selskap.
const Cell = ({ label, value, testId, mono = false, icon: Icon }: { label: string; value: React.ReactNode; testId: string; mono?: boolean; icon?: React.ComponentType<{ className?: string }> }) => (
  <div className="rounded-lg border bg-white px-3 py-2 min-w-0" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-sm font-semibold text-slate-800 flex items-start gap-1 max-w-full ${mono ? 'font-mono text-xs sm:text-sm' : ''}`}>
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-slate-500 mt-0.5" />}<span className="min-w-0 break-words whitespace-normal">{value === 0 ? 0 : (value || '—')}</span>
    </div>
  </div>
);

/** Alle fly på turen m/ Flytid — aldri tak på 4. */
function FlightList({ flights }: { flights: { flight_number: string; arrival_time?: string }[] }) {
  if (!flights.length) return <>—</>;
  return (
    <span className="flex flex-wrap gap-1" data-testid="pl-flight-list">
      {flights.map((f, i) => (
        <span key={`${f.flight_number}-${i}`} className="inline-flex items-baseline gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-xs" data-testid={`pl-flight-${i + 1}`}>
          <span className="font-mono font-semibold">{f.flight_number}</span>
          {f.arrival_time && <span className="font-mono text-[11px] text-slate-600">{f.arrival_time}</span>}
        </span>
      ))}
    </span>
  );
}

// Topptekst i sjåførformat: Dato/Uke/Dag · Fra → Stopp 1–N → Endestopp m/ tider · Sjåfør · Kjøretøy · Flynr./Flytid · Selskap · Tot. Pax · Opptalt.
// Stedene fargekodes som i bookinglisten når de finnes i forkortelsene (locColor fra siden).
export function DriverManifestHeader({ trip, lang, locColor }: { trip: any; lang: string; locColor?: (loc: string | undefined | null) => string | null }) {
  const { t } = useTranslation();
  const colorOf = locColor || (() => null);
  const LocTd = ({ loc, testId }: { loc: string; testId: string }) => {
    const c = loc ? colorOf(loc) : null;
    return (
      <td className="px-2 py-1.5" data-testid={testId}>
        {loc ? <span className={c ? 'inline-block px-1.5 py-0.5 rounded' : ''} style={locationChipStyle(c)} data-color={c || undefined}>{loc}</span> : '—'}
      </td>
    );
  };
  const dayName = (() => {
    try { const n = new Date(`${trip.date}T00:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { weekday: 'long' }); return n.charAt(0).toUpperCase() + n.slice(1); } catch { return trip.day_name; }
  })();
  const stops: any[] = trip.stops || [];
  // Dynamiske stopp-kolonner: minst 3 (gammel mal), flere når turen har flere stopp.
  const stopCols = Array.from({ length: Math.max(3, stops.length) }, (_, i) => ({ label: `${t('driverManifest.stoppOrd', 'Stopp')} ${i + 1}`, loc: stops[i]?.location || '', time: stops[i]?.time || '' }));
  const flights: { flight_number: string; arrival_time?: string }[] = (trip.flights || []).filter((f: any) => f?.flight_number).length
    ? (trip.flights || []).filter((f: any) => f?.flight_number)
    : (trip.flight_numbers || []).map((n: string) => ({ flight_number: n }));
  const counted = trip.checked_in_count || 0, total = trip.total_passengers || trip.seat_count || 0;
  const mismatch: { booking_number: string; names: number; pax: number }[] = trip.pax_mismatch || [];
  return (
    <div className="space-y-3" data-testid="pl-header">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Cell label={t('driverManifest.dato', 'Dato')} value={trip.date} testId="pl-date" mono />
        <Cell label={t('driverManifest.uke', 'Uke')} value={trip.week_number} testId="pl-week" mono />
        <Cell label={t('driverManifest.dag', 'Dag')} value={dayName} testId="pl-day" />
        <Cell label={t('driverManifest.totPax', 'Tot. Pax')} value={total} testId="pl-tot-pax" mono />
        <div className={`rounded-lg border px-3 py-2 ${counted >= total && total > 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="pl-counted">
          <div className="text-[10px] uppercase tracking-wide text-slate-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{t('driverManifest.opptaltPax', 'Opptalt pax')}</div>
          <div className="text-sm font-bold font-mono text-slate-900" data-testid="pl-counted-value">{counted} / {total}</div>
        </div>
        <Cell label={t('driverManifest.kjoretoy', 'Kjøretøy')} value={trip.vehicle} icon={Bus} testId="pl-vehicle" />
      </div>
      {mismatch.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="pl-pax-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
          <span>
            {t('driverManifest.paxAvvik', 'Flere navn enn Tot. Pax – kontroller:')}{' '}
            {mismatch.map((m) => `${m.booking_number} (${m.names} ${t('driverManifest.navn', 'navn')} / ${m.pax} pax)`).join(', ')}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Cell label={t('driverManifest.sjafor', 'Sjåfør')} value={trip.driver_name} icon={User} testId="pl-driver" />
        <Cell label={`${t('driverManifest.flynr', 'Flynr.')} / ${t('driverManifest.flytid', 'Flytid')}`} value={<FlightList flights={flights} />} icon={Plane} testId="pl-flights" />
        <Cell label={t('driverManifest.selskap', 'Selskap')} value={(trip.companies || []).join(', ')} icon={Building2} testId="pl-companies" />
        <Cell label={t('driverManifest.bookinger', 'Bookinger')} value={(trip.bookings || []).length} icon={Users} testId="pl-booking-count" mono />
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white" data-testid="pl-route">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-2 py-1.5 text-left">{t('driverManifest.fra', 'Fra')}</th>
              {stopCols.map((s) => <th key={s.label} className="px-2 py-1.5 text-left">{s.label}</th>)}
              <th className="px-2 py-1.5 text-left">{t('driverManifest.endestopp', 'Endestopp')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="font-semibold text-slate-800">
              <LocTd loc={trip.from_location || ''} testId="pl-from" />
              {stopCols.map((s, i) => <LocTd key={i} loc={s.loc} testId={`pl-stop-${i + 1}`} />)}
              <LocTd loc={trip.to_location || ''} testId="pl-to" />
            </tr>
            <tr className="font-mono text-slate-600">
              <td className="px-2 py-1.5" data-testid="pl-dep-time">{trip.departure_time || ''}</td>
              {stopCols.map((s, i) => <td key={i} className="px-2 py-1.5">{s.time}</td>)}
              <td className="px-2 py-1.5" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
